import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert, Table } from 'react-bootstrap';
import api from '../services/api';
import { showToast } from './ToastNotification';

const formatPrice = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '—';
  return num.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Potvrzení smazání faktury.
 *
 * Mazání je softové — doklad zmizí z výpisů, exportů i provizí, ale zůstane
 * dohledatelný a jde ho vrátit. **Číslo se nerecykluje**, zůstane v účetnictví
 * neobsazené; tím odpadá dřívější ruční přečíslovávání řady.
 *
 * Dialog si načte podklady z `invoices/<id>/delete-info`: varování (zaplacený
 * nebo už odeslaný doklad) a navazující doklady zálohového řetězce.
 */
function InvoiceDeleteModal({ show, invoiceId, onHide, onDeleted }) {
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [reason, setReason] = useState('');
  const [withRelated, setWithRelated] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !invoiceId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      setReason('');
      setWithRelated(false);
      try {
        const res = await api.getInvoiceDeleteInfo(invoiceId);
        if (!cancelled) setInfo(res.data || null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Nepodařilo se načíst podklady ke smazání.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [show, invoiceId]);

  const related = (info?.related || []).filter((r) => !r.deleted);

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const res = await api.deleteInvoice(invoiceId, { reason, withRelated });
      const count = res.data?.count || 1;
      showToast(count === 1 ? 'Faktura byla smazaná.' : `Smazáno ${count} dokladů.`, 'success');
      window.dispatchEvent(new CustomEvent('invoices:refresh'));
      window.dispatchEvent(new CustomEvent('orders:refresh'));
      onHide();
      if (onDeleted) onDeleted(res.data);
    } catch (e) {
      showToast(e.message || 'Fakturu se nepodařilo smazat.', 'danger');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal show={show} onHide={() => !deleting && onHide()} centered>
      <Modal.Header closeButton={!deleting}>
        <Modal.Title><i className="bi bi-trash me-2" />Smazat fakturu</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center py-3"><Spinner animation="border" size="sm" className="me-2" />Načítání…</div>
        ) : info ? (
          <>
            <div className="mb-3">
              Opravdu smazat doklad <strong>{info.number || info.id}</strong>
              {info.type_label ? ` (${info.type_label})` : ''}?
              <div className="small text-muted mt-1">
                {info.customer_name || 'Neznámý zákazník'} · {formatPrice(info.total_price)} {info.currency || 'CZK'}
              </div>
            </div>

            <Alert variant="secondary" className="py-2 small">
              <i className="bi bi-info-circle me-1" />
              Číslo <strong>{info.number}</strong> zůstane nepoužité — v účetnictví ho budete mít jako
              neobsazené. Doklad půjde vrátit z filtru „Smazané".
            </Alert>

            {(info.warnings || []).map((w) => (
              <Alert key={w} variant="warning" className="py-2 small mb-2">
                <i className="bi bi-exclamation-triangle me-1" />{w}
              </Alert>
            ))}

            {related.length > 0 && (
              <>
                <div className="small fw-bold mb-1">Navazující doklady</div>
                <Table size="sm" className="align-middle mb-2">
                  <tbody>
                    {related.map((r) => (
                      <tr key={r.id}>
                        <td className="text-nowrap">{r.number || r.id}</td>
                        <td className="small text-muted">{r.type_label}</td>
                        <td className="text-end text-nowrap">{formatPrice(r.total_price)} {r.currency || 'CZK'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <Form.Check
                  type="checkbox"
                  id="delete-with-related"
                  className="mb-3"
                  checked={withRelated}
                  onChange={(e) => setWithRelated(e.target.checked)}
                  label={`Smazat i tyto doklady (${related.length})`}
                />
              </>
            )}

            <Form.Group>
              <Form.Label className="small fw-bold">Důvod smazání (nepovinné)</Form.Label>
              <Form.Control
                type="text"
                value={reason}
                maxLength={255}
                onChange={(e) => setReason(e.target.value)}
                placeholder="např. inzerát nevyšel, objednávka nebyla stornovaná"
              />
            </Form.Group>
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={deleting}>Zrušit</Button>
        <Button variant="danger" onClick={handleDelete} disabled={loading || deleting || !info}>
          {deleting
            ? <><Spinner animation="border" size="sm" className="me-1" />Mažu…</>
            : <><i className="bi bi-trash me-1" />Smazat</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default InvoiceDeleteModal;
